import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from './apiBaseUrl';
import './LoginPage.css';
import { FullPageLoader, withRouteLoaderState } from './navigationLoader';

const isAllowedTroubleLoginEmail = (email) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const emailWithDomainRegex = /^[^\s@]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
    return emailWithDomainRegex.test(normalizedEmail);
};

const SupportRequestForm = ({
    embedded = false,
    onSuccess,
    onCancel
}) => {
    const VisibilityIcon = ({ visible }) => (
        <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
            focusable="false"
        >
            <path
                d="M2 12C4.4 7.8 8 5.7 12 5.7S19.6 7.8 22 12c-2.4 4.2-6 6.3-10 6.3S4.4 16.2 2 12Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle
                cx="12"
                cy="12"
                r="3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
            />
            {visible ? null : (
                <path
                    d="M4 4l16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                />
            )}
        </svg>
    );

    const [formData, setFormData] = useState({
        email: '',
        contactNo: '',
        panNumber: '',
        issueDescription: ''
    });
    const [popup, setPopup] = useState({ show: false, message: '', type: 'info' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSensitiveFields, setShowSensitiveFields] = useState({
        contactNo: false,
        panNumber: false
    });

    const navigate = useNavigate();
    const showPopup = (message, type = 'info') => setPopup({ show: true, message, type });
    const closePopup = () => setPopup((prev) => ({ ...prev, show: false }));
    const toggleFieldVisibility = (fieldName) => {
        setShowSensitiveFields((prev) => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const recordLead = async (mobile, pan = '') => {
        if (!mobile || mobile.length !== 10) return;
        try {
            const baseUrl = getApiBaseUrl();
            await fetch(`${baseUrl}/api/record-lead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mobile,
                    pan,
                    kioskId: process.env.REACT_APP_KIOSK_ID
                })
            });
        } catch (err) {
            console.warn('Lead capture failed (silent)', err);
        }
    };

    useEffect(() => {
        let timer;
        if (formData.contactNo.length === 10) {
            recordLead(formData.contactNo, formData.panNumber);
            timer = setTimeout(() => {
                recordLead(formData.contactNo, formData.panNumber);
            }, 60000);
        }
        return () => clearTimeout(timer);
    }, [formData.contactNo, formData.panNumber]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'contactNo') {
            const nextMobile = value.replace(/\D/g, '').slice(0, 10);
            setFormData({ ...formData, [name]: nextMobile });
            return;
        }

        if (name === 'panNumber') {
            const nextPan = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
            setFormData({ ...formData, [name]: nextPan });
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(formData.panNumber)) {
            showPopup('Please enter a valid 10-character PAN (e.g. ABCDE1234F).', 'error');
            return;
        }
        if (!/^[6-9]\d{9}$/.test(formData.contactNo)) {
            showPopup('Please provide a valid mobile number.', 'error');
            return;
        }
        if (!isAllowedTroubleLoginEmail(formData.email)) {
            showPopup('Please enter a valid email with a domain extension (e.g. .com, .in).', 'error');
            return;
        }

        setIsSubmitting(true);
        let keepSubmitting = false;
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/trouble-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    kioskId: process.env.REACT_APP_KIOSK_ID || ''
                })
            });
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Unable to submit complaint.');
            }

            if (typeof onSuccess === 'function') {
                onSuccess(data.message || 'Your complaint has been registered.');
            } else {
                showPopup(data.message || 'Your complaint has been registered.', 'success');
                keepSubmitting = true;
                setTimeout(() => navigate('/login', {
                    state: withRouteLoaderState({}, 'Opening login page...')
                }), 1200);
            }
        } catch (error) {
            showPopup(error.message || 'Unable to submit complaint.', 'error');
        } finally {
            if (!keepSubmitting) {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <>
            {isSubmitting && (
                <FullPageLoader message="Submitting your request..." />
            )}
            {popup.show && (
                <div className="popup-overlay">
                    <div className={`popup-box ${popup.type}`}>
                        <div className="popup-icon">
                            {popup.type === 'success' ? '✓' : popup.type === 'error' ? '✕' : '!'}
                        </div>
                        <p>{popup.message}</p>
                        <button onClick={closePopup}>OK</button>
                    </div>
                </div>
            )}

            <div className={embedded ? 'support-form-shell support-form-shell--embedded' : 'login-form-container'}>
                <div className={embedded ? 'support-form-wrapper' : 'form-wrapper'}>
                    {!embedded && <p className="brand-tag">FinnCube Technologies</p>}
                    <h2 className={embedded ? 'support-form-title' : ''}>Trouble Logging In?</h2>
                    <p className={embedded ? 'support-form-subtitle' : 'subtitle'}>Submit your details and we&apos;ll help you get back in.</p>

                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div className="form-group">
                            <label htmlFor="contactNo">Contact Number</label>
                            <div className="input-with-toggle">
                                <input
                                    type={showSensitiveFields.contactNo ? 'tel' : 'password'}
                                    id="contactNo"
                                    name="contactNo"
                                    placeholder="Enter your 10-digit mobile number"
                                    value={formData.contactNo}
                                    onChange={handleChange}
                                    required
                                    pattern="[0-9]{10}"
                                    title="Please enter a valid 10-digit mobile number"
                                    maxLength="10"
                                    inputMode="none"
                                    data-layout="numeric"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                                <button
                                    type="button"
                                    className="visibility-toggle-btn"
                                    onClick={() => toggleFieldVisibility('contactNo')}
                                    aria-label={showSensitiveFields.contactNo ? 'Hide contact number' : 'Show contact number'}
                                    aria-pressed={showSensitiveFields.contactNo}
                                >
                                    <VisibilityIcon visible={showSensitiveFields.contactNo} />
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="Enter your registered email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                pattern="[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                                title="Please enter a valid email (for example: name@example.com)"
                                inputMode="none"
                                data-layout="email"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="panNumber">PAN Number</label>
                            <div className="input-with-toggle">
                                <input
                                    type={showSensitiveFields.panNumber ? 'text' : 'password'}
                                    id="panNumber"
                                    name="panNumber"
                                    placeholder="Enter your 10-character PAN"
                                    value={formData.panNumber}
                                    onChange={handleChange}
                                    required
                                    maxLength="10"
                                    pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}"
                                    title="PAN must be 10 characters in format ABCDE1234F"
                                    autoComplete="new-password"
                                    autoCorrect="off"
                                    autoCapitalize="characters"
                                    spellCheck="false"
                                    inputMode="none"
                                    data-layout="alphanumeric"
                                />
                                <button
                                    type="button"
                                    className="visibility-toggle-btn"
                                    onClick={() => toggleFieldVisibility('panNumber')}
                                    aria-label={showSensitiveFields.panNumber ? 'Hide PAN number' : 'Show PAN number'}
                                    aria-pressed={showSensitiveFields.panNumber}
                                >
                                    <VisibilityIcon visible={showSensitiveFields.panNumber} />
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="issueDescription">Describe the Issue</label>
                            <textarea
                                id="issueDescription"
                                name="issueDescription"
                                placeholder="Tell us what's happening..."
                                value={formData.issueDescription}
                                onChange={handleChange}
                                required
                                style={{ minHeight: '120px' }}
                                inputMode="none"
                                data-layout="alphanumeric"
                            />
                        </div>

                        <div className={embedded ? 'support-form-actions' : undefined}>
                            {embedded && (
                                <button type="button" className="secondary-btn support-form-cancel" onClick={onCancel}>
                                    Cancel
                                </button>
                            )}
                            <button type="submit" className="login-btn support-form-submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : 'Send'}
                            </button>
                        </div>

                        {!embedded && (
                            <div style={{ marginTop: '40px', textAlign: 'center' }}>
                                <Link to="/" className="back-link">← Back to Start</Link>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </>
    );
};

export default SupportRequestForm;
